import React, { useContext, useState } from "react";
import * as Icons from "@mui/icons-material/";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
    AssetSignalsModel,
    IndexModel,
    RenderModel,
    RightPanelsModel,
    UserNotificationDto,
} from "../../../../api/APIClient";
import { fetchAssetInformation, fetchAssetInformationById } from "../../../../api/Fetchers";
import PanelViewerContext from "../../../../Context/PanelViewerProvider";
import Thermometer from "../../../../../assets/spriteIcons/thermometer.svg";
import Circle from "../../../../../assets/spriteIcons/circle.svg";
import Co2 from "../../../../../assets/spriteIcons/co2.svg";
import Sensor from "../../../../../assets/spriteIcons/sensor.svg";
import {
    colorHexToRgb,
    convertHexToRGB,
    ViewerMode,
} from "../ForgeViewer/BIMDigitalTwinViewer/Utils";
import { HeatmapBehaviorResolver } from "../ForgeViewer/BIMDigitalTwinViewer/HeatmapAnimator";
import { aforoSimulator } from "../Aforo/aforoSimulatorInstance";
import { reservasShadingValue } from "../Aforo/aforoConfig";
import { energySimulator } from "../Energia/energySimulatorInstance";
import { isEnergySensorType } from "../Energia/energyConfig";
import { ProjectContext, ProjectModelContext } from "../../Context/ProjectContext";
import PanelProjectContext from "../../../PanelProjectProvider";

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: AssetSignalsModel;
}

interface IPanelProjectContext {
    setDbidsToRender: React.Dispatch<React.SetStateAction<number[]>>;
    setHoveredDeviceInfo: React.Dispatch<React.SetStateAction<IObjTest | undefined>>;
    setRightPanelsData: React.Dispatch<React.SetStateAction<RightPanelsModel | undefined>>;
    setIsRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isViewerLoading: boolean;
    setIsViewerLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRenderingItems: React.Dispatch<React.SetStateAction<boolean>>;
    setDbidFromSelection: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    setAssetSelection: React.Dispatch<React.SetStateAction<IndexModel | undefined>>;
    setViewerMode: React.Dispatch<React.SetStateAction<ViewerMode>>;
    setIsWorkOrderOpen: React.Dispatch<React.SetStateAction<boolean>>;
    notifications: UserNotificationDto[];
}

const useAsset = () => {
    const project = useContext(ProjectContext);
    const model = useContext(ProjectModelContext);

    const { enqueueSnackbar } = useSnackbar();
    const {
        setHoveredDeviceInfo,
        setRightPanelsData,
        setIsRightPanelOpen,
        isViewerLoading,
        setIsViewerLoading,
        setIsRenderingItems,
        setDbidFromSelection,
        setAssetSelection,
        setViewerMode,
        setIsWorkOrderOpen,
        notifications,
        setDbidsToRender,
    } = useContext<IPanelProjectContext>(PanelProjectContext);

    const { t } = useTranslation("project");

    // usePureEffect(() => {
    //     if (isViewerLoading) {
    //         setAssetSelection({});
    //         setIsRightPanelOpen(false);
    //         setIsWorkOrderOpen(false);
    //         setIsRenderingItems(false);
    //     }
    // }, [isViewerLoading]);

    async function PrintDeviceIcons(res: AssetSignalsModel[]) {
        window.NOP_VIEWER.getExtension("Autodesk.DataVisualization");
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const devices = res;

        const SensorStyleDefinitions = {
            sensor: {
                url: Sensor,
            },
            co2: {
                url: Co2,
            },
            temperature: {
                url: Thermometer,
            },
            default: {
                url: Circle,
            },
        };

        const dataVizExt = window.NOP_VIEWER.getExtension(
            "Autodesk.DataVisualization"
        ) as Autodesk.Extensions.DataVisualization;
        const DATAVIZEXTN = Autodesk.DataVisualization.Core;
        const viewableType = DATAVIZEXTN.ViewableType.SPRITE;
        const styleMap = {};

        dataVizExt.removeAllViewables();

        // // Create model-to-style map from style definitions.
        const viewableData = new DATAVIZEXTN.ViewableData();

        viewableData.spriteSize = 30;
        if (devices && devices.length > 0) {
            devices.forEach((device) => {
                if (device.dbIds && device.dbIds.length > 0) {
                    Object.entries(SensorStyleDefinitions).forEach(([type, styleDef]) => {
                        styleMap[type] = new DATAVIZEXTN.ViewableStyle(
                            viewableType,
                            new THREE.Color(convertHexToRGB(device.icon?.color)),
                            styleDef.url
                        );
                    });

                    // eslint-disable-next-line
                    const style = styleMap[device.icon?.type!] || styleMap["default"];
                    // eslint-disable-next-line
                    window.NOP_VIEWER.addFrags(device.dbIds!);
                    const dbIdBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                        // eslint-disable-next-line
                        device?.dbIds!
                    );
                    const dbIdCenter = dbIdBox.center();
                    const viewable = new DATAVIZEXTN.SpriteViewable(
                        dbIdCenter,
                        style,
                        device.dbIds![0]
                    );
                    viewableData.addViewable(viewable);
                }
            });

            let click = false;

            const onItemHovering = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    if (event.hovering) {
                        const deviceId = event.dbId;
                        const device = devices.find((elm) => elm.dbIds?.includes(deviceId));
                        if (device) {
                            const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                                device.dbIds!
                            );
                            const deviceCenter = deviceBox?.center();
                            const mappedPosition =
                                window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                            const objTest: IObjTest = {
                                xcoord: mappedPosition.x,
                                ycoord: mappedPosition.y - 10,
                                data: device,
                            };
                            setHoveredDeviceInfo(objTest);
                        }
                    } else if (click === false) {
                        setHoveredDeviceInfo(undefined);
                    }
                }
            };

            const onItemClick = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    const deviceId = event.dbId;
                    const device = devices.find((elm) => elm.dbIds?.includes(deviceId));
                    if (device) {
                        const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                            device.dbIds!
                        );
                        const deviceCenter = deviceBox?.center();
                        const mappedPosition = window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                        const objTest: IObjTest = {
                            xcoord: mappedPosition.x,
                            ycoord: mappedPosition.y - 10,
                            data: device,
                        };
                        click = true;
                        console.log("clicked", click);
                    }

                    if (deviceId === 0) {
                        setHoveredDeviceInfo(undefined);
                        click = false;
                    }
                }
            };

            await viewableData.finish();
            setIsViewerLoading(false);
            const DataVizCore = Autodesk.DataVisualization.Core;

            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemHovering);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_HOVERING, onItemHovering);

            dataVizExt.addViewables(viewableData);
        }
    }

    // Simulated behavior for each demo sensor. Tube point ids end with their
    // index along the pipe, so an increasing phase per index makes the heat
    // peak travel through the tube (flow effect).
    const heatmapBehaviorResolver: HeatmapBehaviorResolver = (pointId, sensorType) => {
        if (isEnergySensorType(sensorType) && pointId.startsWith("Aforo")) {
            // Eficiencia energética sobre los puntos de aforo: la magnitud de la
            // zona (los ids de zona no llevan "_", así que es el primer tramo
            // tras el prefijo). Las plazas de parking van una a una (cargador).
            const rest = pointId.substring(pointId.indexOf("_") + 1);
            const parts = rest.split("_");
            const zoneId = parts[0];
            const plaza = pointId.startsWith("AforoPlaza_") ? parseInt(parts[1], 10) : undefined;
            return {
                kind: "custom",
                getValue: () =>
                    energySimulator.getHeatValue(
                        zoneId,
                        sensorType,
                        Number.isNaN(plaza) ? undefined : plaza
                    ),
            };
        }
        if (sensorType === "reservas" && pointId.startsWith("Aforo")) {
            // Vista de RESERVAS: lo reservado en el tramo elegido, sin mezclar con
            // la ocupación. Ids: <Prefijo>_<zona>_<índice>[...]; el carril del
            // parking (AforoFixed) queda gris.
            if (pointId.startsWith("AforoFixed_")) {
                return { kind: "steady", value: 0 };
            }
            const rest = pointId.substring(pointId.indexOf("_") + 1);
            const separator = rest.lastIndexOf("_");
            const zoneId = separator > 0 ? rest.substring(0, separator) : rest;
            if (pointId.startsWith("AforoPlaza_")) {
                const plaza = parseInt(rest.substring(separator + 1), 10) || 0;
                return {
                    kind: "custom",
                    getValue: () =>
                        reservasShadingValue(aforoSimulator.isPlazaReserved(zoneId, plaza) ? 1 : 0),
                };
            }
            return {
                kind: "custom",
                getValue: () => reservasShadingValue(aforoSimulator.getZoneReservedRatio(zoneId)),
            };
        }
        if (pointId.startsWith("AforoPlaza_")) {
            // Plaza de parking: 1 ocupada (rojo), 0 libre (verde).
            // Id: AforoPlaza_<zona>_<plaza>-<sensor>; todos los sensores de una
            // plaza comparten su estado.
            const rest = pointId.substring("AforoPlaza_".length);
            const separator = rest.lastIndexOf("_");
            const zoneId = separator > 0 ? rest.substring(0, separator) : rest;
            const plaza = parseInt(rest.substring(separator + 1), 10) || 0;
            return { kind: "custom", getValue: () => aforoSimulator.getPlazaValue(zoneId, plaza) };
        }
        if (pointId.startsWith("AforoFixed_")) {
            // Relleno con valor fijo: AforoFixed_<zona>_<índice>_<tanto por ciento>.
            const pct = Number(pointId.substring(pointId.lastIndexOf("_") + 1)) || 0;
            return { kind: "steady", value: Math.max(0, Math.min(1, pct / 100)) };
        }
        if (pointId.startsWith("AforoFocus_")) {
            // Foco de zona (estilo difuso): el nivel de ocupación de la zona, con
            // una ligera variación fija por foco para que las zonas grandes no
            // sean una mancha uniforme, y un vaivén suave para que tenga vida.
            const rest = pointId.substring("AforoFocus_".length);
            const separator = rest.lastIndexOf("_");
            const zoneId = separator > 0 ? rest.substring(0, separator) : rest;
            const index = Number(rest.substring(separator + 1)) || 0;
            const spread = 0.88 + 0.24 * (((index * 7919) % 13) / 12);
            return {
                kind: "custom",
                getValue: () => {
                    // En una evacuación el foco vale según la gente que tiene
                    // cerca en ese instante: el mapa sigue a las personas.
                    const evacuacion = aforoSimulator.getFocusValue(zoneId, index);
                    if (evacuacion !== null) return evacuacion;
                    const ratio = aforoSimulator.getZoneRatio(zoneId);
                    const wobble = 0.04 * Math.sin(performance.now() / 2500 + index * 1.7);
                    return Math.max(0, Math.min(1, ratio * spread + (ratio > 0 ? wobble : 0)));
                },
            };
        }
        if (pointId.startsWith("AforoSeat_")) {
            // Persona simulada: el id es AforoSeat_<zona>_<asiento>. Vale 1 si
            // el simulador pone a alguien en ese asiento ahora y -1 (el
            // sombreador lo ignora) si está vacío.
            const rest = pointId.substring("AforoSeat_".length);
            const separator = rest.lastIndexOf("_");
            const zoneId = separator > 0 ? rest.substring(0, separator) : rest;
            const seat = Number(rest.substring(separator + 1));
            return { kind: "custom", getValue: () => aforoSimulator.getSeatValue(zoneId, seat) };
        }
        if (pointId.startsWith("Aforo_")) {
            // Malla de fondo frío de cada zona: siempre 0 (azul). El color lo
            // ponen las personas; el nivel de aforo lo cuentan los chips.
            return { kind: "steady", value: 0 };
        }
        const flowPhase = Number(pointId.split("_")[1] ?? 0) * 0.9;
        if (pointId.startsWith("Tube1")) {
            return { kind: "wave", min: 0, max: 0.25, periodSeconds: 6, phase: flowPhase };
        }
        if (pointId.startsWith("Tube2")) {
            return { kind: "wave", min: 0.75, max: 1, periodSeconds: 6, phase: flowPhase };
        }
        if (pointId.startsWith("Solar")) {
            // Continuous wave field over world coordinates: ripples sweep
            // across each panel's surface and travel from panel to panel.
            return {
                kind: "field",
                min: 0.35,
                max: 1,
                wavelength: window.NOP_VIEWER.solarFieldWavelength,
                periodSeconds: 8,
            };
        }
        return { kind: "randomWalk", min: 0.1, max: 0.9, retargetSeconds: 4, wobble: 0.05 };
    };

    /**
     * La extensión DataVisualization dibuja el sombreado como COPIAS de la
     * geometría (suelo incluido) en una capa superpuesta, exactamente en el
     * mismo plano que el original. Con la cámara cenital las dos superficies
     * pelean por la profundidad y el suelo original asoma en manchas que
     * cambian al mover la cámara. Un polygonOffset negativo acerca un poco
     * las copias a la cámara y gana siempre el sombreado. updateSurfaceShading
     * reutiliza los materiales, así que basta con aplicarlo tras cada render.
     */
    const liftSurfaceShadingOverlay = () => {
        const impl = window.NOP_VIEWER.impl as unknown as {
            overlayScenes?: {
                [name: string]: { scene?: { children?: Array<{ material?: THREE.Material }> } };
            };
            invalidate: (a: boolean, b: boolean, c: boolean) => void;
        };
        const overlay = impl.overlayScenes?.DataVizSurfaceShading;
        overlay?.scene?.children?.forEach((mesh) => {
            const material = mesh.material as
                | (THREE.Material & {
                      polygonOffset?: boolean;
                      polygonOffsetFactor?: number;
                      polygonOffsetUnits?: number;
                      needsUpdate?: boolean;
                  })
                | undefined;
            if (!material || material.polygonOffset) return;
            material.polygonOffset = true;
            material.polygonOffsetFactor = -1;
            material.polygonOffsetUnits = -4;
            material.needsUpdate = true;
        });
        impl.invalidate(false, false, true);
    };

    async function createHeatmaps(group: string, type: string, radius: number) {
        const dataVizExtn = window.NOP_VIEWER.getExtension(
            "Autodesk.DataVisualization"
        ) as Autodesk.Extensions.DataVisualization;
        await window.NOP_VIEWER.loadHeatmapsByGroups();
        const animator = window.NOP_VIEWER.heatmapAnimator;
        // Ajustes por grupo y tipo de sensor (p. ej. "First floor:occupancy" para el
        // aforo sobre el mismo nodo que confort), con los del grupo como respaldo.
        const typed = `${group}:${type}`;
        const renderOptions = {
            heatmapConfig: {
                confidence:
                    window.NOP_VIEWER.heatmapConfidenceByGroup[typed] ??
                    window.NOP_VIEWER.heatmapConfidenceByGroup[group] ??
                    radius,
                powerParameter:
                    window.NOP_VIEWER.heatmapPowerByGroup[typed] ??
                    window.NOP_VIEWER.heatmapPowerByGroup[group] ??
                    2,
            },
        };
        // start() must run before the render so sampleValue has its resolver;
        // the first animation tick fires on the next frame, after the render.
        animator.start(dataVizExtn, heatmapBehaviorResolver, renderOptions);
        dataVizExtn.renderSurfaceShading(group, type, animator.sampleValue, renderOptions);
        liftSurfaceShadingOverlay();
    }

    /** Quita el mapa de calor activo y para su animación (vistas sin sombreado). */
    function removeHeatmaps() {
        const viewer = window.NOP_VIEWER;
        const dataVizExtn = viewer?.getExtension("Autodesk.DataVisualization") as
            | Autodesk.Extensions.DataVisualization
            | undefined;
        viewer?.heatmapAnimator.stop();
        if (dataVizExtn && viewer?.model) {
            dataVizExtn.removeSurfaceShading(viewer.model);
        }
    }

    // async function PrintChatIcon(res: AssetSignalsModel[]) {}

    const fetchingAssetInformationById = (projectId, assetId) => {
        setIsViewerLoading(true);
        setIsRightPanelOpen(false);
        setDbidsToRender([]);
        // Modo del visor al pedir la ficha: si cambia mientras llega la respuesta (el usuario
        // o el avatar han abierto otro módulo), el resultado tardío no debe pintarse encima.
        const modeAtRequest = window.NOP_VIEWER.mode;
        fetchAssetInformationById(projectId, assetId)
            .then((res) => {
                if (window.NOP_VIEWER.mode !== modeAtRequest) {
                    setIsViewerLoading(false);
                    return;
                }
                if (res.status === "success") {
                    console.log("res.data", res.data.rightPanels?.documents?.data);
                    setAssetSelection(res.data.index!);
                    setIsRenderingItems(false);
                    setIsWorkOrderOpen(false);
                    const renderInfo: RenderModel[] = res.data.render!;
                    if (renderInfo.length > 0) {
                        window.NOP_VIEWER.resetModel(false);
                        window.NOP_VIEWER.setOpacityModel(0.3);
                        window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                        setViewerMode(window.NOP_VIEWER.mode);
                        const dbidsToRender = renderInfo.map((element) => {
                            const hexColor = element.style?.hexColor;
                            const rgbColor = colorHexToRgb(hexColor!);
                            const covertedColor = [
                                rgbColor.r * 255,
                                rgbColor.g * 255,
                                rgbColor.b * 255,
                            ];
                            // window.NOP_VIEWER.setDbIdsColorByHex(element.dbIds!, hexColor!);
                            window.NOP_VIEWER.setElementsSpecularColor(
                                covertedColor,
                                element.dbIds!
                            );
                            return element.dbIds;
                        });
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const fullArrayOfDbidToRender: (number | undefined)[] =
                            dbidsToRender.flat();
                        window.NOP_VIEWER.fitToView(fullArrayOfDbidToRender as number[]);
                        setDbidsToRender(fullArrayOfDbidToRender as number[]);

                        // Html tooltips
                        if (
                            renderInfo &&
                            renderInfo.every((d) => d.dbIds !== null && d.dbIds !== undefined)
                        ) {
                            setIsRenderingItems(true);
                            const dbIds = renderInfo?.map((d) => d.dbIds!);
                            let allDbIds: number[] = [];
                            dbIds?.forEach((arr) => {
                                allDbIds = [...allDbIds, ...arr];
                            });
                            window.NOP_VIEWER.clearAllLabels();
                            renderInfo?.forEach((e) => {
                                window.NOP_VIEWER.addLabelHeatMap(
                                    e.dbIds!,
                                    e.tooltip,
                                    e.style?.hexColor,
                                    undefined,
                                    true
                                );
                                if (
                                    notifications.filter((x) => x.titleParams === assetId)?.length >
                                    0
                                ) {
                                    window.NOP_VIEWER.addLabelHeatMap(
                                        e.dbIds!,
                                        {
                                            type: "chat",
                                            // content: `Notificaciones: ${
                                            //     notifications.filter((x) => x.titleParams === assetId)
                                            //         ?.length
                                            // }`,
                                            // content: `Tienes ${notifications
                                            //     .filter((x) => x.titleParams === assetId)
                                            //     ?.length.toString()} notificación/es sin leer`,
                                            content: notifications
                                                .filter((x) => x.titleParams === assetId)[0]
                                                .ids.split(",")
                                                .length.toString(),
                                        },
                                        "#FFB959",
                                        undefined,
                                        true
                                    );
                                }
                            });
                        }

                        // Signal icons and tooltips
                        if (
                            res.data.rightPanels?.signals?.data &&
                            res.data.rightPanels?.signals?.data.length > 0
                        ) {
                            PrintDeviceIcons(res.data.rightPanels?.signals?.data);
                        } else {
                            setIsViewerLoading(false);
                            window.NOP_VIEWER.deleteDeviceIcons();
                        }
                    } else {
                        enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                            variant: "warning",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        setIsViewerLoading(false);
                    }
                    // Right panels
                    if (res.data.rightPanels) {
                        setRightPanelsData(res.data.rightPanels);
                        setIsRightPanelOpen(true);
                    } else {
                        setIsRightPanelOpen(false);
                    }
                }
            })
            .catch((err) => {
                enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                setIsViewerLoading(false);
            });
    };

    const fetchingAssetInformationByDbId = (event) => {
        if (event.dbIdArray[0]) {
            setIsViewerLoading(true);
            setDbidFromSelection(event.dbIdArray);
            setDbidsToRender([]);
            // Igual que arriba: si se cambia de módulo durante la consulta, se descarta.
            const modeAtRequest = window.NOP_VIEWER.mode;
            fetchAssetInformation(project.id, event.dbIdArray)
                .then((res) => {
                    if (window.NOP_VIEWER.mode !== modeAtRequest) {
                        setIsViewerLoading(false);
                        return;
                    }
                    if (res.status === "success") {
                        setIsWorkOrderOpen(false);

                        if (res.data.index!) {
                            setAssetSelection(res.data.index!);
                        }

                        const renderInfo: RenderModel[] = res.data.render!;
                        if (
                            renderInfo.length > 0 &&
                            window.NOP_VIEWER.mode !== ViewerMode.AssetControl
                        ) {
                            setIsRenderingItems(false);
                            window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                            setViewerMode(window.NOP_VIEWER.mode);
                            window.NOP_VIEWER.resetModel(false);
                            window.NOP_VIEWER.setOpacityModel(0.2);
                            const dbidsToRender = renderInfo.map((element) => {
                                const hexColor = element.style?.hexColor;
                                const rgbColor = colorHexToRgb(hexColor!);
                                const covertedColor = [
                                    rgbColor.r * 255,
                                    rgbColor.g * 255,
                                    rgbColor.b * 255,
                                ];
                                // window.NOP_VIEWER.setDbIdsColorByHex(element.dbIds!, hexColor!);
                                window.NOP_VIEWER.setElementsSpecularColor(
                                    covertedColor,
                                    element.dbIds!
                                );

                                return element.dbIds;
                            });
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const fullArrayOfDbidToRender: (number | undefined)[] =
                                dbidsToRender.flat();
                            window.NOP_VIEWER.fitToView(fullArrayOfDbidToRender as number[]);
                            setDbidsToRender(fullArrayOfDbidToRender as number[]);

                            // Html tooltips
                            if (
                                renderInfo &&
                                renderInfo.every((d) => d.dbIds !== null && d.dbIds !== undefined)
                            ) {
                                setIsRenderingItems(true);
                                const dbIds = renderInfo?.map((d) => d.dbIds!);
                                let allDbIds: number[] = [];
                                dbIds?.forEach((arr) => {
                                    allDbIds = [...allDbIds, ...arr];
                                });
                                // window.NOP_VIEWER.setOpacityModel(0.1);
                                window.NOP_VIEWER.clearAllLabels();
                                // window.NOP_VIEWER.deleteElementsSpecularColor();
                                renderInfo?.forEach((e) => {
                                    window.NOP_VIEWER.addLabelHeatMap(
                                        e.dbIds!,
                                        e.tooltip,
                                        e.style?.hexColor,
                                        undefined,
                                        true
                                    );
                                });
                            }

                            // Signal icons and tooltips
                            if (
                                res.data.rightPanels?.signals?.data &&
                                res.data.rightPanels?.signals?.data.length > 0
                            ) {
                                PrintDeviceIcons(res.data.rightPanels?.signals?.data);
                            } else {
                                setIsViewerLoading(false);
                                window.NOP_VIEWER.deleteDeviceIcons();
                            }
                        } else if (window.NOP_VIEWER.mode !== ViewerMode.AssetControl) {
                            enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                                variant: "warning",
                                anchorOrigin: {
                                    horizontal: "right",
                                    vertical: "bottom",
                                },
                            });
                            setIsViewerLoading(false);
                            setIsRenderingItems(false);
                        } else {
                            setIsViewerLoading(false);
                        }
                        // Right panels
                        if (res.data.rightPanels) {
                            setIsRightPanelOpen(true);
                            setRightPanelsData(res.data.rightPanels);
                        } else {
                            setIsRightPanelOpen(false);
                            setRightPanelsData(undefined);
                        }
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(`${t("viewer.messages.assetNotFound")}`, {
                        variant: "warning",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    });
                    setIsViewerLoading(false);
                });
        }
    };

    const handleModelClick = (event) => {
        fetchingAssetInformationByDbId(event);
        console.log(event);
    };

    return {
        fetchingAssetInformationById,
        fetchingAssetInformationByDbId,
        handleModelClick,
        // loadHeatmapsByGroups,
        createHeatmaps,
        removeHeatmaps,
    };
};

export default useAsset;
